import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';
import { getScoringTracking } from '../../services/scoringService';

const TrackingEventos = ({ user, onLogout, onNavigate }) => {
  //console.log('Usuario actual:', user);
  const [grupo, setGrupo] = useState('');
  const [orden, setOrden] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [casoEncontrado, setCasoEncontrado] = useState(null);
  const [trackingEventos, setTrackingEventos] = useState([]);
  const [error, setError] = useState(null);

  // Determinar categoría según el estado del evento
  const determinarCategoria = (eventDescription) => {
    const desc = eventDescription.toUpperCase();
    if (desc.includes('APROBADO') || desc.includes('COMPLETADO') || desc.includes('ÉXITO')) {
      return 'success';
    } else if (desc.includes('RECHAZADO') || desc.includes('ERROR') || desc.includes('FALLIDO')) {
      return 'error';
    } else if (desc.includes('PENDIENTE') || desc.includes('REVISION') || desc.includes('INCOMPLETO')) {
      return 'warning';
    }
    return 'info';
  };

  // Formatear fecha para display
  const formatearFecha = (fechaStr) => {
    try {
      // La fecha viene en formato "2026-06-16 10:36:43"
      const [fecha, hora] = fechaStr.split(' ');
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}/${year} ${hora}`;
    } catch (error) {
      return fechaStr;
    }
  };

  // Calcular tiempo total entre primer y último evento
  const calcularTiempoTotal = (eventos) => {
    if (eventos.length < 2) return 'N/A';
    
    try {
      const primerEvento = new Date(eventos[eventos.length - 1].eventDate);
      const ultimoEvento = new Date(eventos[0].eventDate);
      const diffMs = ultimoEvento - primerEvento;
      
      const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const horas = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (dias > 0) {
        return `${dias} día${dias > 1 ? 's' : ''} ${horas}h ${minutos}min`;
      } else if (horas > 0) {
        return `${horas}h ${minutos}min`;
      } else {
        return `${minutos} min`;
      }
    } catch (error) {
      return 'N/A';
    }
  };

  const handleBuscar = async () => {
    if (!grupo && !orden) {
      setError('Por favor, complete al menos un campo de búsqueda');
      return;
    }
    
    setBuscando(true);
    setError(null);
    
    try {
      // Llamar al API real
      const eventos = await getScoringTracking(grupo, orden, (user.ce_ID !== null ? user.ce_ID : 0));
      
      if (!eventos || eventos.length === 0) {
        setError('No se encontraron eventos para el grupo y orden especificados');
        setCasoEncontrado(null);
        setTrackingEventos([]);
        return;
      }

      // Ordenar eventos por fecha descendente (más reciente primero)
      const eventosOrdenados = [...eventos].sort((a, b) => 
        new Date(b.eventDate) - new Date(a.eventDate)
      );

      // Mapear eventos del API al formato del componente
      const eventosMapeados = eventosOrdenados.map((evento, index) => ({
        id: index + 1,
        evento: evento.eventDescription,
        fecha: formatearFecha(evento.eventDate),
        usuario: evento.userName,
        detalle: evento.notes || 'Sin notas adicionales',
        categoria: determinarCategoria(evento.eventDescription)
      }));

      // Crear resumen del caso
      const ultimoEvento = eventosOrdenados[0];
      const resumenCaso = {
        grupo: eventos[0].group,
        orden: eventos[0].order,
        estado: ultimoEvento.eventDescription,
        totalEventos: eventos.length,
        ultimaActualizacion: formatearFecha(ultimoEvento.eventDate),
        tiempoTotal: calcularTiempoTotal(eventosOrdenados)
      };

      setCasoEncontrado(resumenCaso);
      setTrackingEventos(eventosMapeados);
      
    } catch (error) {
      console.error('Error al buscar tracking:', error);
      setError(error.message || 'Error al obtener el tracking de eventos');
      setCasoEncontrado(null);
      setTrackingEventos([]);
    } finally {
      setBuscando(false);
    }
  };

  const getCategoriaColor = (categoria) => {
    switch(categoria) {
      case 'success':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getCategoriaIcono = (categoria) => {
    switch(categoria) {
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const getEstadoColor = (estado) => {
    switch(estado) {
      case 'APROBADO':
        return 'text-green-600 bg-green-100';
      case 'RECHAZADO':
        return 'text-red-600 bg-red-100';
      case 'PENDIENTE':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-3 py-4">
        {/* Título */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">
              Tracking de Eventos
            </h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Visualice el historial completo de eventos de un caso</p>
        </div>

        {/* Filtros de búsqueda mejorados */}
        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-20 blur-3xl"></div>
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Grupo</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Ingrese número de grupo"
                    value={grupo}
                    onChange={(e) => {
                      const sanitized = sanitizeInput(e.target.value);
                      setGrupo(sanitized);
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200 group-hover:border-gray-300"
                  />
                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">tag</span>
                </div>
              </div>
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Orden</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Ingrese número de orden"
                    value={orden}
                    onChange={(e) => {
                      const sanitized = sanitizeInput(e.target.value);
                      setOrden(sanitized);
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200 group-hover:border-gray-300"
                  />
                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">receipt_long</span>
                </div>
              </div>
              <div>
                <button
                  onClick={handleBuscar}
                  disabled={buscando}
                  className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-all duration-200 text-sm font-semibold shadow-lg shadow-red-200 hover:shadow-xl hover:shadow-red-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                >
                  <span className="material-icons text-sm">{buscando ? 'hourglass_empty' : 'search'}</span>
                  {buscando ? 'Buscando...' : 'Buscar Caso'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mensaje de error */}
        {error && !buscando && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl shadow-lg overflow-hidden mb-8 animate-fade-in">
            <div className="px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="material-icons text-red-600 text-xl">error_outline</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-red-800 mb-1">Error al buscar</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center transition-colors"
                >
                  <span className="material-icons text-red-600 text-sm">close</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Resultados de búsqueda y Tracking */}
        {casoEncontrado && (
          <div className="space-y-8 animate-fade-in">
            {/* Resumen del caso mejorado */}
            <div className="bg-gradient-to-br from-white via-white to-red-50 border border-gray-200 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-30 blur-3xl"></div>
              
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-3 relative">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <span className="material-icons text-white text-xl">assignment</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Resumen del Caso</h3>
                    <p className="text-red-100 text-xs">Información general del expediente</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 relative">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">folder</span>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Grupo</label>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{casoEncontrado.grupo}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">receipt</span>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Orden</label>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{casoEncontrado.orden}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">flag</span>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</label>
                    </div>
                    <span className={`inline-flex px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${getEstadoColor(casoEncontrado.estado)}`}>
                      {casoEncontrado.estado}
                    </span>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">event_note</span>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Eventos</label>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{casoEncontrado.totalEventos}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline de eventos mejorado */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white bg-opacity-10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <span className="material-icons text-white text-xl">timeline</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-white">Línea de Tiempo</h3>
                      <p className="text-gray-300 text-xs">Historial completo de eventos del caso</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-10 px-3 py-1 rounded-lg backdrop-blur-sm">
                    <span className="text-white text-xs font-semibold">{trackingEventos.length} eventos</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="relative">
                  {/* Línea continua de fondo */}
                  <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-black"></div>
                  
                  <div className="space-y-0">
                    {trackingEventos.map((track, idx) => (
                      <div key={track.id} className="relative flex items-center gap-4">
                        {/* Icono circular */}
                        <div className={`relative flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 z-10 ${track.categoria === 'success' ? 'bg-green-500 border-green-600' : track.categoria === 'warning' ? 'bg-yellow-500 border-yellow-600' : track.categoria === 'error' ? 'bg-red-500 border-red-600' : 'bg-blue-500 border-blue-600'}`}>
                          <span className="material-icons text-white text-lg">{getCategoriaIcono(track.categoria)}</span>
                        </div>
                      
                      {/* Rectángulo con contenido */}
                      <div className="flex-1 bg-gray-50 hover:bg-gray-100 rounded-xl p-4 border border-gray-200 hover:border-red-300 transition-all duration-200 hover:shadow-md my-2">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm">{track.evento}</h4>
                        </div>
                        
                        <p className="text-xs text-gray-600 mb-2">{track.detalle}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <span className="material-icons" style={{ fontSize: '14px' }}>schedule</span>
                            <span>{track.fecha}</span>
                          </div>
                          
                          {/*Ocultar el usuario para el concesionario*/}
                          {user.group_ID != "1" && (
                            <div className="flex items-center gap-1">
                              <span className="material-icons" style={{ fontSize: '14px' }}>person</span>
                              <span>{track.usuario}</span>
                            </div>
                          )}

                          {idx === 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md font-medium">
                              <span className="material-icons" style={{ fontSize: '12px' }}>fiber_manual_record</span>
                              Reciente
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              </div>

              {/* Footer simplificado */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  Total de eventos registrados: <span className="font-semibold text-gray-700">{trackingEventos.length}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Estado sin búsqueda mejorado */}
        {!casoEncontrado && !buscando && (
          <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-dashed border-gray-300 rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-16 text-center relative">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-transparent opacity-30"></div>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-4 shadow-inner">
                  <span className="material-icons text-6xl text-gray-400">history</span>
                </div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">No hay resultados para mostrar</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Ingrese un número de grupo o de orden en los campos de búsqueda y presione el botón <span className="font-semibold">"Buscar Caso"</span> para visualizar el tracking de eventos.
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                  <span className="material-icons text-sm">info</span>
                  <span>Complete al menos un campo para realizar la búsqueda</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Estado de carga mejorado */}
        {buscando && (
          <HondaLoader 
            message="Buscando información..." 
            variant="inline"
            size="medium"
            subtitle="Estamos procesando su solicitud y recuperando el historial de eventos del caso"
          />
        )}
      </div>
    </MainLayout>
  );
};

export default TrackingEventos;

